import {
  Button,
  Clickable,
  Dialog,
  ForEach,
  Icon,
  IconButton,
  If,
  P,
  Person,
  SearchInput,
  Tabs,
  Tooltip,
} from '@fashionnovateam/nova-ui-toolkit';
import { useEffect, useState } from 'react';
import { useCounterContext } from '../hooks/useCounter';
import { Counter, VendorContact } from '../types';
import { useUpdateContacts } from '../hooks/queries';
import toast from '../../Toast/Toast';
import { Contact } from '../../../Lib/Api/vendor';

function CounterContactList(props: { counter: Counter; userId: number }) {
  const { counter, userId } = props;

  const canRemove =
    counter.vendor_contacts && counter.vendor_contacts.length > 1;
  const members = [
    ...(counter.fn_contacts?.map((c) => ({ ...c, type: 'fn' as const })) || []),
    ...(counter.vendor_contacts?.map((c) => ({
      ...c,
      type: 'vendor' as const,
    })) || []),
  ];

  const [removeMember, setRemoveMember] = useState<VendorContact | null>(null);
  const updateContacts = useUpdateContacts();

  const removeContact = async () => {
    if (!removeMember) return;
    try {
      await updateContacts.mutateAsync({
        userId,
        counterId: counter.counter_id,
        data: {
          vendor_contacts:
            counter.vendor_contacts?.filter((c) => c.id !== removeMember.id) ||
            [],
        },
      });
      setRemoveMember(null);
      toast(
        { title: `${removeMember.full_name} was removed.` },
        { type: 'success' },
      );
    } catch (error) {
      console.error('Failed to remove contact:', error);
      toast({ title: 'Failed to remove member' }, { type: 'error' });
    }
  };

  return (
    <>
      <Dialog isOpen={!!removeMember} onClose={() => setRemoveMember(null)}>
        <Dialog.Header />
        <Dialog.Content className="w-full">
          <div className="flex flex-col gap-6 w-full h-full">
            <Dialog.Title>
              Remove {removeMember?.full_name} from this conversation
            </Dialog.Title>
            <P size="xsm" color="disabled">
              {removeMember?.full_name} will lose the ability to:
              <br />
              - View all historical messages in this conversation.
              <br />
              - Send messages to Fashion Nova.
              <br />
              - Send and update the Quote.
              <br />- Take actions and update the status for Vendor Sample and
              Prev Sample.
            </P>
          </div>
        </Dialog.Content>
        <Dialog.Footer className="gap-2">
          <Button color="secondary" onClick={() => setRemoveMember(null)}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={removeContact}
            loading={updateContacts.isLoading}
          >
            Yes, Remove
          </Button>
        </Dialog.Footer>
      </Dialog>
      <div className="flex flex-col w-full">
        <ForEach items={members} empty="No members in this Counter">
          {(member) => (
            <div
              key={member.id}
              className="flex flex-row w-full py-4 border-b-2 border-b-neutral-200"
            >
              <div className="flex-grow">
                <Person
                  name={member.full_name}
                  badgeType={member.type === 'fn' ? 'fn_internal' : 'vendor'}
                />
              </div>
              <If condition={member.type === 'vendor'}>
                {canRemove ? (
                  <IconButton
                    icon={<Icon.DeleteDustbin02 />}
                    label="Remove member"
                    color="secondary"
                    size="sm"
                    onClick={() => setRemoveMember(member)}
                  />
                ) : (
                  <Tooltip
                    content="You must have at least 1 member of your team assigned to this counter."
                    zIndex={50}
                    className="max-w-48"
                  >
                    <IconButton
                      icon={<Icon.DeleteDustbin02 />}
                      label="Remove member"
                      color="secondary"
                      size="sm"
                      disabled
                    />
                  </Tooltip>
                )}
              </If>
            </div>
          )}
        </ForEach>
      </div>
    </>
  );
}

function CounterContactAdd(props: {
  counter: Counter;
  userId: number;
  contacts: Contact[];
}) {
  const { counter, userId, contacts } = props;

  const [query, setQuery] = useState('');

  const filteredContacts = contacts.filter(
    (c) =>
      c.role !== 'VENDOR_OPERATIONS' &&
      !c.deleted_at &&
      !counter.vendor_contacts?.find((vc) => vc.id === c.id.toString()) &&
      (`${c.first_name} ${c.last_name}`
        .toLowerCase()
        .includes(query.toLowerCase()) ||
        c.email.toLowerCase().includes(query.toLowerCase())),
  );

  const [addMember, setAddMember] = useState<Contact | null>(null);
  const updateContacts = useUpdateContacts();

  const addContact = async () => {
    if (!addMember) return;
    try {
      await updateContacts.mutateAsync({
        userId,
        counterId: counter.counter_id,
        data: {
          vendor_contacts: [
            ...(counter.vendor_contacts || []),
            {
              id: addMember.id.toString(),
              full_name: `${addMember.first_name} ${addMember.last_name}`,
              email: addMember.email,
            },
          ],
        },
      });
      setAddMember(null);
      toast(
        {
          title: `${addMember.first_name} ${addMember.last_name} was added.`,
        },
        { type: 'success' },
      );
    } catch (error) {
      console.error('Failed to add contact:', error);
      toast({ title: 'Failed to add member' }, { type: 'error' });
    }
  };

  return (
    <>
      <Dialog isOpen={!!addMember} onClose={() => setAddMember(null)}>
        <Dialog.Header />
        <Dialog.Content className="w-full">
          <div className="flex flex-col gap-6 w-full h-full">
            <Dialog.Title>
              Add {addMember?.first_name} {addMember?.last_name} to this
              conversation
            </Dialog.Title>
            <P size="xsm" color="disabled">
              {addMember?.first_name} {addMember?.last_name} will have the
              ability to:
              <br />
              - View all historical messages in this conversation.
              <br />
              - Send messages to Fashion Nova.
              <br />
              - Send and update the Quote.
              <br />- Take actions and update the status for Vendor Sample and
              Prev Sample.
            </P>
          </div>
        </Dialog.Content>
        <Dialog.Footer className="gap-2">
          <Button color="secondary" onClick={() => setAddMember(null)}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={addContact}
            loading={updateContacts.isLoading}
          >
            Yes, Add
          </Button>
        </Dialog.Footer>
      </Dialog>
      <SearchInput value={query} onChangeValue={setQuery} />
      <div className="flex flex-col w-full">
        <ForEach items={filteredContacts} empty="No contact found">
          {(contact) => (
            <Clickable
              key={contact.id}
              className="flex flex-row w-full py-4 border-b-2 border-b-neutral-200"
              onClick={() => setAddMember(contact)}
            >
              <Person
                name={`${contact.first_name} ${contact.last_name}`}
                badgeType="vendor"
              />
            </Clickable>
          )}
        </ForEach>
      </div>
    </>
  );
}

function CounterContactDialog({
  handleClose,
  open,
}: {
  handleClose: () => void;
  open: boolean;
}) {
  const { selectedCounter, userId, contacts } = useCounterContext();
  const [tab, setTab] = useState<'list' | 'add'>('list');
  useEffect(() => {
    setTab('list');
  }, [open]);

  if (!selectedCounter || !userId) return null;

  return (
    <Dialog isOpen={open} onClose={handleClose}>
      <Dialog.Header />
      <Dialog.Content className="w-full">
        <div className="flex flex-col gap-6 w-full h-full">
          <Dialog.Title>Manage Members</Dialog.Title>
          <Tabs.Group type="segment">
            <Clickable onClick={() => setTab('list')}>
              <Tabs.Item
                icon="chat/chat-default"
                type="segment"
                isActive={tab === 'list'}
              >
                Team members
              </Tabs.Item>
            </Clickable>
            <Clickable onClick={() => setTab('add')}>
              <Tabs.Item
                icon="plus/plus-circle"
                type="segment"
                isActive={tab === 'add'}
              >
                Add Members
              </Tabs.Item>
            </Clickable>
          </Tabs.Group>
          {tab === 'list' ? (
            <CounterContactList counter={selectedCounter} userId={userId} />
          ) : (
            <CounterContactAdd
              counter={selectedCounter}
              userId={userId}
              contacts={contacts}
            />
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
}

export default CounterContactDialog;
